import { Button } from "@/projects/NPS/Stepper/common/button";
const heroImage = "/assets/Rectangle 92.png";
import { useEffect, useState } from "react";
import CommonModal from "@/projects/NPS/Stepper/common/Modal";
import OtpVerification from "../OtpVerification";
import InitialRegistrationModal from "../SubscriberRegistration/InitialRegistrationModal";
import OtpVerificationOfEmailAndMobile from "../OtpVerificationOfEmailAndMobile";
import { useNavigate } from "react-router-dom";
import { navigationRoutes } from "@/utils/routes";
import {
  getAccessToken,
  getUserInfoFromLocalStorage,
} from "@/lib/storeManager";
import GetRegistrationDetailsService from "@/services/NPS/Stepper/APIs/getRegistrationDetailsService"; // Make sure to import your service
import InitialProcessRegistrationService from "@/services/NPS/Stepper/APIs/InitialProcessRegistrationService";
import NpsUserNavigationRoutes from "@/projects/NPS/NpsUser/utils/navigationRoutes";
import { useRegistrationDetails } from "@/projects/NPS/Stepper/context/registrationDetails";

const HeroSection = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [registrationData, setRegistrationData] = useState<{
    pan: string;
    fullName: string;
    dob: string;
    mobile: string;
    email: string;
    code_reference: string;
  } | null>(null);
  const [isInitialOpen, setIsInitialOpen] = useState(false);
  const [saveEmail, setSaveEmail] = useState<string | null>(null);
  const [
    isOtpVerificationOfEmailAndMobileOpen,
    setIsOtpVerificationOfEmailAndMobileOpen,
  ] = useState(false);

  // Use registration details hook to get registration status
  const { registrationDetails } = useRegistrationDetails();

  useEffect(() => {
    const token = getAccessToken();
    if (token) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  useEffect(() => {
    const userInfo = getUserInfoFromLocalStorage();
    setUserInfo(userInfo);
  }, [isInitialOpen, isOtpVerificationOfEmailAndMobileOpen]);

  const handleApplyNowClick = async () => {
    if (!isLoggedIn) {
      setIsOtpOpen(true);
      return;
    }
    try {
      const registrationResponse =
        await InitialProcessRegistrationService.getRegistrationDetails();
      if (registrationResponse.success === false) {
        if (userInfo?.is_email_verified) {
          setIsInitialOpen(true);
        } else {
          setIsOtpVerificationOfEmailAndMobileOpen(true);
        }
        return;
      }
      const registrationDetailsResponse =
        await GetRegistrationDetailsService.getRegistrationDetails();
      if (registrationDetailsResponse.success === false) {
        navigate(navigationRoutes.NEW_SUBSCRIBER_REGISTRATION);
      } else {
        // Check if registration is completed and payment is successful
        const registration = registrationDetailsResponse.registration;
        if (
          registration?.status === "in_progress" &&
          registration?.personalDtls?.payment_status === "success"
        ) {
          navigate(navigationRoutes.PRAN_SUCCESS_SCREEN);
        } else if (
          registration?.status === "completed" &&
          registration?.provider_response?.status === "Pending for Registration"
        ) {
          navigate(navigationRoutes.PRAN_SUCCESS_SCREEN);
        } else if (
          registration?.status === "payment_pending" &&
          registration?.personalDtls?.payment_status === "pending"
        ) {
          navigate(navigationRoutes.PRAN_SUCCESS_SCREEN);
        } else {
          navigate(navigationRoutes.NEW_SUBSCRIBER_REGISTRATION);
        }
      }
    } catch (error) {
      console.error("Error fetching registration details:", error);
    }
  };

  const handleGoToDashboardClick = () => {
    navigate("/nps-user" + NpsUserNavigationRoutes.NPS_OVERVIEW);
  };

  const handleContinueToApplyClick = () => {
    navigate(navigationRoutes.NEW_SUBSCRIBER_REGISTRATION);
  };

  return (
    <section
      id="hero-section"
      className="relative max-h-[500px] overflow-hidden bg-cover bg-center bg-no-repeat px-4 lg:min-h-[650px] lg:px-10"
      style={{
        backgroundImage: `url("${heroImage}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-l from-white/0 via-gray-800/60 to-teal-950/95" />

      {/* Content */}
      <div className="container relative z-10 mx-auto py-16 lg:py-24">
        <div className="grid min-h-[500px] items-center gap-12 lg:grid-cols-2">
          {/* Left Text Side */}
          <div className="space-y-6">
            <div className="max-w-lg font-nunito text-[25px] font-extrabold capitalize not-italic tracking-[1px] text-white md:text-[50px] md:leading-[60px] md:tracking-[-1px]">
              Invest to Secure your retirement & Save taxes
            </div>

            <p className="max-w-xl font-nunito text-[16px] font-medium leading-snug text-[#f5f5f5]">
              Seamlessly manage and track all your investments with cutting-edge
              technology and smart tools - empowering you to make informed
              decisions for a secure and prosperous financial future.
            </p>

            <div className="flex flex-col gap-4 sm:flex-row">
              {/* Show Apply Now button only if:
                  1. User is not logged in, OR
                  2. User is logged in but registrationDetails is null (success: false)
              */}
              {(!isLoggedIn || !registrationDetails) && (
                <Button
                  className="hover:bg-light/90 h-[50px] rounded-[8px] border-0 bg-[var(--Light,#1DAB91)] px-8 py-4 font-nunito text-xl font-semibold text-white"
                  onClick={handleApplyNowClick}
                >
                  Apply Now
                </Button>
              )}

              {/* Show Continue to Apply button if:
                  1. User is logged in AND
                  2. Registration exists (success: true) AND
                  3. Registration status is NOT "completed"
              */}
              {isLoggedIn &&
                registrationDetails &&
                registrationDetails.status !== "completed" && (
                  <Button
                    className="hover:bg-light/90 h-[50px] rounded-[8px] border-0 bg-[var(--Light,#1DAB91)] px-8 py-4 font-nunito text-xl font-semibold text-white"
                    onClick={handleContinueToApplyClick}
                  >
                    Continue to Apply
                  </Button>
                )}

              {/* Show Go to Dashboard button only if:
                  1. User is logged in AND
                  2. Registration exists AND
                  3. Registration status is "completed"
              */}
              {isLoggedIn &&
                registrationDetails &&
                registrationDetails.status === "completed" && (
                  <Button
                    variant="outline"
                    className="h-[50px] bg-white px-8 py-4 font-nunito text-xl font-semibold leading-loose text-[#266985] hover:bg-white/90 hover:text-[#266985]"
                    onClick={handleGoToDashboardClick}
                  >
                    Go to Dashboard
                  </Button>
                )}
            </div>

            {isLoggedIn && (
              <div>
                <ul className="m-0 list-disc pl-5" />
              </div>
            )}
          </div>

          {/* Empty Right Side (kept same structure) */}
          <div className="relative" />
        </div>
      </div>

      {/* OTP Modal */}
      <CommonModal
        isOpen={isOtpOpen}
        onClose={() => setIsOtpOpen(false)}
        width="w-full sm:w-[600px]"
        height="h-[auto]"
        padding="p-2 sm:p-6"
      >
        <OtpVerification
          onClose={() => setIsOtpOpen(false)}
          onSuccess={() => {
            setIsOtpOpen(false);
            window.location.reload();
          }}
        />
      </CommonModal>

      {/* Initial Registration Modal */}
      <CommonModal
        isOpen={isInitialOpen}
        onClose={() => setIsInitialOpen(false)}
        width="w-full sm:w-[600px]"
        height="h-[auto]"
        padding="p-2 sm:p-6"
      >
        <InitialRegistrationModal
          saveEmail={saveEmail}
          isOpen={isInitialOpen}
          onFormSubmit={async data => {
            setRegistrationData(data);
          }}
          setIsOpen={setIsInitialOpen}
          onSubmit={async () => {
            setIsInitialOpen(false);
            navigate(navigationRoutes.NEW_SUBSCRIBER_REGISTRATION);
          }}
        />
      </CommonModal>

      {/* OTP Email & Mobile Verification Modal */}
      <CommonModal
        isOpen={isOtpVerificationOfEmailAndMobileOpen}
        onClose={() => setIsOtpVerificationOfEmailAndMobileOpen(false)}
        width="w-full sm:w-[600px]"
        height="h-[auto]"
        padding="p-2 sm:p-6"
      >
        <OtpVerificationOfEmailAndMobile
          setSaveEmail={setSaveEmail}
          onClose={() => setIsOtpVerificationOfEmailAndMobileOpen(false)}
          registrationData={registrationData}
          onSubmit={() => {
            setIsOtpVerificationOfEmailAndMobileOpen(false);
            setIsInitialOpen(true);
          }}
        />
      </CommonModal>
    </section>
  );
};

export default HeroSection;
